import React from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { ObjectWrapperProps } from "@autoform/react";

import { CuratedBadge } from "@/components/field-curation/curated-badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
} from "@/components/ui/item";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ISO_RE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

function formatScalar(value: unknown, depth = 0): string {
  if (value === null || value === undefined || value === "") return "";

  if (typeof value === "boolean") return value ? "Yes" : "No";

  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    const hasObjects = value.some(
      (item) => typeof item === "object" && item !== null,
    );
    if (hasObjects)
      return `${value.length} item${value.length !== 1 ? "s" : ""}`;
    return value.join(", ");
  }

  if (typeof value === "object") {
    if (depth > 0) return "{…}";
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, v]) => v !== null && v !== undefined && v !== "",
    );
    if (entries.length === 0) return "";
    return entries
      .map(([k, v]) => {
        const formatted = formatScalar(v, depth + 1);
        return formatted ? `${k}: ${formatted}` : null;
      })
      .filter(Boolean)
      .join("  ·  ");
  }

  if (typeof value === "string") {
    if (ISO_RE.test(value)) {
      return new Date(value).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });
    }
  }

  return String(value);
}

// ---------------------------------------------------------------------------
// ValuePreview — renders a single value with appropriate styling
// ---------------------------------------------------------------------------

function ValuePreview({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted-foreground/50 italic">—</span>;
  }

  // Boolean → colored badge
  if (typeof value === "boolean") {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 font-medium",
          value ? "text-emerald-500" : "text-muted-foreground",
        )}
      >
        <span
          className={cn(
            "size-1.5 rounded-full",
            value ? "bg-emerald-500" : "bg-muted-foreground/50",
          )}
        />
        {value ? "Yes" : "No"}
      </span>
    );
  }

  // Array
  if (Array.isArray(value)) {
    if (value.length === 0)
      return <span className="text-muted-foreground/50 italic">—</span>;

    const hasObjects = value.some(
      (item) => typeof item === "object" && item !== null,
    );
    if (hasObjects) {
      return (
        <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
          {value.length} item{value.length !== 1 ? "s" : ""}
        </span>
      );
    }
    return (
      <span className="truncate text-foreground/80">
        {(value as unknown[]).join(", ")}
      </span>
    );
  }

  // Object
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, v]) => v !== null && v !== undefined && v !== "",
    );
    if (entries.length === 0)
      return <span className="text-muted-foreground/50 italic">—</span>;
    return (
      <span className="truncate text-foreground/80">
        {entries
          .slice(0, 2)
          .map(([k, v]) => {
            const formatted = formatScalar(v, 1);
            return formatted ? `${k}: ${formatted}` : null;
          })
          .filter(Boolean)
          .join("  ·  ")}
        {entries.length > 2 && (
          <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
            and {entries.length - 2} more key
            {entries.length - 2 !== 1 ? "s" : ""}
          </span>
        )}
      </span>
    );
  }

  // ISO date string → human-readable
  if (typeof value === "string" && ISO_RE.test(value)) {
    return (
      <span className="text-foreground/80">
        {new Date(value).toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        })}
      </span>
    );
  }

  // URL → show hostname + pathname (truncated), clickable
  if (
    typeof value === "string" &&
    (value.startsWith("https://") || value.startsWith("http://"))
  ) {
    try {
      const url = new URL(value);
      const display =
        url.pathname && url.pathname !== "/"
          ? `${url.hostname}${url.pathname}`
          : url.hostname;
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="truncate font-mono text-xs text-sky-400 hover:underline"
        >
          {display}
        </a>
      );
    } catch {
      // fall through to generic string
    }
  }

  return <span className="truncate text-foreground/80">{String(value)}</span>;
}

// ---------------------------------------------------------------------------
// CustomObjectWrapper
// ---------------------------------------------------------------------------

const ObjectPreview = ({ props }: { props: { path: string[] } }) => {
  const fieldLabel = props.path[props.path.length - 1];
  const value = useWatch({ name: props.path.join(".") });
  return (
    <React.Fragment>
      <span className="text-muted-foreground capitalize whitespace-nowrap pt-px">
        {fieldLabel}
      </span>
      <span className="min-w-0 truncate">
        <ValuePreview value={value} />
      </span>
    </React.Fragment>
  );
};
export const CustomObjectWrapper = ({
  field,
  children,
  label,
}: ObjectWrapperProps) => {
  const form = useFormContext();

  const hackedChildren = children as {
    key: string;
    props: { path: string[] };
  }[];

  const path = (hackedChildren?.[0]?.props?.path.slice(0, -1) || []).join(".");

  const isInvalid = Object.keys(form.formState.errors).includes(field.key);

  const onCancel = () => {
    console.debug("Resetting field", field.key);
    form.resetField(path ?? field.key);
  };

  const onRemove = () => {
    console.debug("Removing field", field.key);
    form.setValue(path ?? field.key, null);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <div>
          <Label>{label}</Label>
          {/* `path` is this object's OWN dotted path (computed above from a
              child's path, minus the child's own segment) — not `field.key`,
              which is only the leaf name and would match a curated name by
              coincidence for an object nested inside another one (e.g. an
              unmodelled `metadata.faq`). `useFieldCuration` already refuses a
              key with a `.` in it, so passing the full path is what makes
              that guard see a nested object as nested. An object with no
              children yet (nothing to read a path from) falls back to
              `field.key`, assuming top-level — the same assumption a
              brand-new empty array makes in `ArrayWrapper`. */}
          <CuratedBadge fieldKey={path || field.key} />
          <Item variant="muted" className="">
            <ItemContent>
              <ItemDescription>{field.description}</ItemDescription>
              {hackedChildren && (
                <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs overflow-hidden">
                  {hackedChildren.map(({ key, props }) => (
                    <ObjectPreview key={key} props={props} />
                  ))}
                </div>
              )}
            </ItemContent>
            <ItemActions>
              <Button variant="outline" size="sm" type="button">
                Edit
              </Button>
            </ItemActions>
          </Item>
          {isInvalid && (
            <span className="text-destructive text-xs mt-1">Invalid</span>
          )}
        </div>
      </DialogTrigger>
      <DialogContent className="max-w-full max-h-screen overflow-auto">
        <DialogHeader>
          <DialogTitle>Edit {label}</DialogTitle>
        </DialogHeader>
        {children}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="destructive" onClick={onRemove}>
              Remove
            </Button>
          </DialogClose>
          <DialogClose asChild>
            <Button variant="outline" onClick={onCancel}>
              Reset
            </Button>
          </DialogClose>
          <DialogClose asChild>
            <Button>Save changes</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
